import { Injectable, inject } from '@angular/core';
import { type Params } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, merge, type Observable, of } from 'rxjs';
import {
    concatMap,
    debounceTime,
    delay,
    filter,
    map,
    mergeMap,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';

import { isUuid } from '@common/helpers';
import type { Id, Uuid } from '@common/types';
import { type SiteContentEntity } from '@common/types/site-content.type';

import { AppFacade } from '@web/store/app/app.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';

import { PagesHttpService } from '@web/store/website/pages/pages.http.service';

import { ContentService } from './content.service';
import { ContentHttpService } from './content.http.service';

import { ThemeStateService } from '@sites/site-theme/src/state';
import * as CartIconActions from '@web/store/shop-cart-icon/shop-cart-icon.actions';
import BlocksActions from '@web/store/page-structure/blocks/blocks.actions';
import SectionsActions from '@web/store/page-structure/sections/sections.actions';
import ColumnsActions from '@web/store/page-structure/columns/columns.actions';
import TabsActions from '@web/store/page-structure/tabs/tabs.actions';
import GridsActions from '@web/store/page-structure/grids/grids.actions';
import WidgetsLegacyActions, { WidgetsActions } from '@web/store/page-structure/widgets/widgets.actions';
import SiteActions from '@web/store/website/site/site.actions';
import HistoryActions from '@web/store/history/history.actions';
import ContentAction from './content.actions';

type PageContentRead = { content: SiteContentEntity; pageId?: Id | Uuid; version?: string };

@Injectable()
export class ContentEffects {
    private readonly actions$ = inject(Actions);
    private readonly themeState = inject(ThemeStateService);
    private readonly contentFacade = inject(ContentFacade);
    private readonly contentService = inject(ContentService);
    private readonly contentHttpService = inject(ContentHttpService);
    private readonly pagesHttpService = inject(PagesHttpService);
    private readonly routerFacade = inject(RouterFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly appFacade: AppFacade = inject(AppFacade);

    public readonly loadContent$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContentAction.loadContent, ContentAction.loadPageContentRequest),
            withLatestFrom(this.contentFacade.siteContent$, (action, siteContent) => ({
                siteId: action.siteId,
                pageId: action.pageId,
                siteContent: 'reloadSite' in action && action.reloadSite ? null : siteContent,
                template: action.template,
            })),
            mergeMap(({ siteId, pageId, siteContent, template }) => {
                const sourcesObject: { site?: Observable<SiteContentEntity>; page?: Observable<PageContentRead> } = {};

                if (template) {
                    if (siteContent) {
                        sourcesObject.site = of(siteContent);
                    } else if (siteId && !isUuid(siteId)) {
                        sourcesObject.site = this.contentHttpService
                            .getSiteContent(siteId)
                            .pipe(map((response) => this.contentService.normalizeSiteContent(response)));
                    } else {
                        sourcesObject.site = of(this.contentService.createEmptySiteContentFromTemplate(template));
                    }

                    if (pageId && !isUuid(pageId)) {
                        sourcesObject.page = this.readPageContent(siteId, pageId);
                    } else {
                        sourcesObject.page = of({
                            content: this.contentService.createEmptyPageContentFromTemplate(template),
                        });
                    }

                    return forkJoin(sourcesObject);
                }

                if (siteContent) {
                    sourcesObject.site = of(siteContent);
                } else if (siteId && !isUuid(siteId)) {
                    sourcesObject.site = this.contentHttpService
                        .getSiteContent(siteId)
                        .pipe(map((response) => this.contentService.normalizeSiteContent(response)));
                } else {
                    sourcesObject.site = of(this.contentService.createEmptySiteContent());
                }

                if (pageId && !isUuid(pageId)) {
                    sourcesObject.page = this.readPageContent(siteId, pageId);
                } else {
                    sourcesObject.page = of({ content: this.contentService.createEmptyPageContent() });
                }

                return forkJoin(sourcesObject);
            }),
            switchMap(({ site, page: { content, pageId, version } }) => [
                ...(version ? [ContentAction.setPageVersion({ pageId, version })] : []),
                ContentAction.loadSiteContentSuccess({ content: site }),
                ContentAction.loadPagesContentSuccess({ content }),
                ContentAction.setContent({ siteContent: site, pageContent: content }),
            ])
        )
    );

    /**
     * The page's version is read BEFORE its content, never alongside: a write landing between the two
     * reads then costs a needless 409 on the next save, where the other order would pair the newer
     * version with the older content and let that save silently overwrite the write.
     */
    private readPageContent(siteId: Id | Uuid, pageId: Id | Uuid): Observable<PageContentRead> {
        return this.pagesHttpService.getSitePage(siteId, pageId as Id).pipe(
            concatMap((page) =>
                this.contentHttpService.getSitePageContent(siteId, pageId).pipe(
                    map((response) => ({
                        content: this.contentService.normalizePageContent(response),
                        pageId,
                        version: page?.version,
                    }))
                )
            )
        );
    }

    // public readonly loadContentFromTemplate$ = createEffect(() =>
    //     this.actions$.pipe(
    //         ofType(ContentAction.loadContentFromTemplate),
    //         map(({ template }) => {
    //             const siteContent = this.contentService.createEmptySiteContentFromTemplate(template);
    //             const pageContent = this.contentService.createEmptyPageContentFromTemplate(template);
    //
    //             return ContentAction.setContent({ siteContent, pageContent });
    //         }),
    //     ),
    // );

    public readonly setContent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ContentAction.setContent),
                withLatestFrom(this.siteFacade.isNewSite$),
                map(([{ siteContent, pageContent }, isNewSite]) => {
                    this.contentService.setInitialContent(siteContent, pageContent, isNewSite);
                })
            ),
        { dispatch: false }
    );

    private readonly AUTO_SAVE_INTERVAL = 1000 * 3;
    public readonly setIsDirty$ = createEffect(() =>
        // The theme is no longer part of the NgRx store, so its edits arrive as their own stream
        // rather than as actions; merged in here so a colour change still marks the page dirty.
        merge(
            this.themeState.changes$,
            this.actions$.pipe(
                ofType(
                    ContentAction.triggerSaveContent,
                    // An undo or redo always changes the page, but it restores through actions of its
                    // own (`addBlock`, `addColumn`, a theme part…) that the list below does not name.
                    HistoryActions.undoSuccess,
                    HistoryActions.redoSuccess,
                    BlocksActions.blockCreated,
                    BlocksActions.removeBlock,
                    BlocksActions.blockCopied,
                    BlocksActions.updateBlockProperties,
                    BlocksActions.moveDown,
                    BlocksActions.moveUp,
                    BlocksActions.hideBlock,
                    BlocksActions.showBlock,
                    SectionsActions.sectionCreated,
                    SectionsActions.removeSection,
                    SectionsActions.moveDown,
                    SectionsActions.moveUp,
                    SectionsActions.updateSectionProperties,
                    ColumnsActions.columnCreated,
                    ColumnsActions.columnCopied,
                    ColumnsActions.columnMoved,
                    ColumnsActions.removeColumn,
                    ColumnsActions.updateColumnProperties,
                    ColumnsActions.updateColumnsProperties,
                    TabsActions.added,
                    TabsActions.created,
                    TabsActions.copied,
                    TabsActions.removed,
                    TabsActions.updateProperties,
                    TabsActions.appendChild,
                    GridsActions.created,
                    GridsActions.remove,
                    GridsActions.updateProperties,
                    GridsActions.moveWidgetFromColumnToGrid,
                    GridsActions.moveWidgetFromGridToGrid,
                    GridsActions.moveWidgetFromGridToColumn,
                    WidgetsLegacyActions.widgetCreated,
                    WidgetsLegacyActions.widgetCreatedToGrid,
                    WidgetsLegacyActions.copyWidget,
                    WidgetsLegacyActions.widgetMoved,
                    WidgetsLegacyActions.updateWidgetProperties,
                    WidgetsActions.updateProperties,
                    WidgetsActions.created,
                    WidgetsActions.added,
                    WidgetsActions.copied,
                    WidgetsActions.removed,
                    WidgetsLegacyActions.updateZeroWidgetProperties,
                    WidgetsLegacyActions.updateWidgetData,
                    WidgetsLegacyActions.updateWidgetStyles,
                    WidgetsLegacyActions.updateWidgetDisplayId,
                    WidgetsLegacyActions.updateGalleryImageData,
                    WidgetsLegacyActions.removeWidget,
                    WidgetsLegacyActions.addWidget,
                    WidgetsLegacyActions.removeWidgetFromGrid,
                    CartIconActions.saveCartIcon
                )
            )
        ).pipe(
            withLatestFrom(this.routerFacade.routerParams$, (_, routerParams: Params) => routerParams),
            concatMap((routerParams) =>
                this.siteFacade.isSaving$.pipe(
                    filter((isSaving) => !isSaving),
                    take(1),
                    delay(0),
                    withLatestFrom(this.contentFacade.isDirty$, (_, isDirty) => ({ isDirty, routerParams }))
                )
            ),
            tap(({ isDirty, routerParams }) => {
                this.contentFacade.setDirtyPageId(routerParams.pageId);

                if (!isDirty) {
                    this.contentFacade.setIsDirty(true);
                }
            }),
            map(({ routerParams }) => routerParams.pageId),
            debounceTime(this.AUTO_SAVE_INTERVAL),
            withLatestFrom(
                this.appFacade.isAdmin$,
                this.routerFacade.routerParams$,
                this.contentFacade.dirtyPageId$,
                this.contentFacade.isAutoSavePaused$,
                (capturedPageId, isAdmin, currentRouterParams, dirtyPageId, isAutoSavePaused) => ({
                    isAdmin,
                    capturedPageId,
                    currentRouterParams,
                    dirtyPageId,
                    isAutoSavePaused,
                })
            ),
            filter(({ isAdmin, capturedPageId, dirtyPageId, currentRouterParams, isAutoSavePaused }) => {
                if (isAutoSavePaused) {
                    return false;
                }

                if (isAdmin) {
                    return true;
                }

                return (
                    Boolean(capturedPageId) &&
                    capturedPageId === dirtyPageId &&
                    capturedPageId === currentRouterParams.pageId
                );
            }),
            map(({ capturedPageId }) => SiteActions.saveSite({ capturedPageId: capturedPageId }))
        )
    );

    public readonly setCopiedSharedBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.blockCopied),
            filter(({ copiedBlock }) => Boolean(copiedBlock.meta.isShared)),
            map(({ copiedBlock }) =>
                ContentAction.setSharedBlock({ blockId: copiedBlock.id, blockType: copiedBlock.meta.role })
            )
        )
    );

    public readonly setCreatedSharedBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.blockCreated),
            filter(({ block }) => Boolean(block.meta.isShared)),
            map(({ block }) => ContentAction.setSharedBlock({ blockId: block.id, blockType: block.meta.role }))
        )
    );

    public readonly removeSharedBlock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BlocksActions.removeBlock),
            filter(({ block }) => Boolean(block.meta.isShared)),
            map(({ block }) => ContentAction.removeSharedBlock({ blockType: block.meta.role }))
        )
    );
}
